import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CustomerHitPayload, SearchHit } from '@monrdv/shared';
import { ApiError, api, request } from '../lib/api.js';

/**
 * Raising a job.
 *
 * A route, like the customer form, and for the same reasons — plus one this screen has of its
 * own: it is reached from three places (the jobs list, a customer's file, the board) and two of
 * them already know the customer. `?customerId=` carries that, so arriving from a customer's
 * page skips the search entirely, and arriving from the list does not.
 *
 * **The customer is chosen by searching, not by scrolling.** There are 6 481 of them; a select
 * element is not a control for that. The global search index already matches on name, phone,
 * address and email, which is how a coordinator actually identifies somebody — usually from a
 * number on a caller display — so this reuses it rather than inventing a narrower lookup.
 *
 * **The stage is not asked for.** The API defaults it to the earliest column on the board, and
 * a coordinator taking a call should not have to classify the work before writing down what the
 * customer wants. Moving it is one click on the job itself, which is where that decision
 * belongs.
 */

type CustomerDetail = {
  id: string;
  displayName: string;
  organization: string | null;
  addresses: Array<{ id: string; line1: string; line2: string | null; city: string; kind: string; isPrimary: boolean }>;
};

const SOURCES = ['phone', 'web', 'walk_in', 'app', 'api'] as const;

export function JobNewScreen() {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const navigate = useNavigate();
  const client = useQueryClient();
  const search = useSearch({ strict: false }) as { customerId?: string };

  const [customerId, setCustomerId] = useState<string | null>(search.customerId ?? null);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [source, setSource] = useState<(typeof SOURCES)[number]>('phone');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  /*
   * The chosen customer, read in full rather than carried over from the search hit.
   *
   * The hit knows a name and a city; this needs the addresses, and reading them from the
   * canonical endpoint means the list of places to send somebody is the same list the customer
   * file shows. It also revalidates the id that arrived in the URL — a stale link should say
   * "not found", not create a job against nothing.
   */
  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => request<CustomerDetail>(`/customers/${customerId}`),
    enabled: !!customerId,
  });

  const addresses = customer.data?.addresses ?? [];

  // Default to the primary service address once the customer resolves. Only ever sets a value
  // the user has not chosen — reselecting the customer clears it below.
  useEffect(() => {
    if (addressId || !addresses.length) return;
    setAddressId((addresses.find((a) => a.isPrimary) ?? addresses[0])!.id);
  }, [addresses, addressId]);

  const create = useMutation({
    mutationFn: () =>
      request<{ id: string; number: string }>('/jobs', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify({
          customerId,
          addressId: addressId ?? undefined,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          internalNote: internalNote.trim() || undefined,
          source,
        }),
      }),
    onSuccess: (created) => {
      // The board, the list and the customer's own page all show this now.
      void client.invalidateQueries({ queryKey: ['jobs'] });
      void client.invalidateQueries({ queryKey: ['board'] });
      void client.invalidateQueries({ queryKey: ['customer', customerId] });
      void client.invalidateQueries({ queryKey: ['search'] });
      void navigate({ to: '/jobs/$jobId', params: { jobId: created.id } });
    },
    onError: (err) => {
      setError(err instanceof ApiError && err.message ? err.message : t('job.form.failed'));
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setIdempotencyKey(crypto.randomUUID());
      }
    },
  });

  const busy = create.isPending;
  const ready = !!customerId && !customer.isPending && !customer.isError;

  return (
    <form
      className="recform"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (ready) create.mutate();
      }}
      noValidate
    >
      <header className="recform__head">
        <h1 className="recform__title">{t('job.form.new')}</h1>
        <p className="recform__lede">{t('job.form.lede')}</p>
      </header>

      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      <fieldset className="recform__section" disabled={busy}>
        <legend className="recform__legend">{t('customer.one')}</legend>

        {customerId && customer.data ? (
          <div className="recform__field">
            <span className="recform__label">{t('customer.one')}</span>
            <div className="recform__row">
              <strong>{customer.data.displayName}</strong>
              <span className="recform__hint">{customer.data.organization ?? ''}</span>
              <button
                type="button"
                className="recform__add"
                onClick={() => {
                  setCustomerId(null);
                  // The previous customer's address must not survive the change; the API would
                  // refuse it as another customer's, and silently keeping it is worse.
                  setAddressId(null);
                }}
              >
                {t('job.form.changeCustomer')}
              </button>
            </div>
          </div>
        ) : customerId && customer.isError ? (
          <p className="recform__error" role="alert">
            {t('job.form.customerGone')}{' '}
            <button type="button" className="recform__add" onClick={() => setCustomerId(null)}>
              {t('job.form.pickAnother')}
            </button>
          </p>
        ) : (
          <CustomerPicker
            t={t}
            onPick={(id) => {
              setCustomerId(id);
              setAddressId(null);
            }}
          />
        )}

        {ready && (
          <div className="recform__field">
            <label className="recform__label" htmlFor="addressId">
              {t('customer.address')}
            </label>
            {addresses.length ? (
              <select
                id="addressId"
                className="recform__select"
                value={addressId ?? ''}
                onChange={(e) => setAddressId(e.target.value || null)}
              >
                {addresses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.line1, a.line2, a.city].filter(Boolean).join(', ')}
                  </option>
                ))}
                {/* A job without a place is legitimate — a phone diagnosis, a quote to prepare. */}
                <option value="">{t('job.form.noAddress')}</option>
              </select>
            ) : (
              <p className="recform__notice">{t('job.form.customerHasNoAddress')}</p>
            )}
          </div>
        )}
      </fieldset>

      <fieldset className="recform__section" disabled={busy || !ready}>
        <legend className="recform__legend">{t('job.form.work')}</legend>

        <div className="recform__field">
          <label className="recform__label" htmlFor="title">
            {t('job.form.title')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <input
            id="title"
            className="recform__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('job.form.titlePlaceholder')}
            autoComplete="off"
          />
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="description">
            {t('job.form.description')}
          </label>
          <textarea
            id="description"
            className="recform__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('job.form.descriptionPlaceholder')}
          />
          {/* Their words, not a summary: it is what the technician reads before knocking. */}
          <span className="recform__hint">{t('job.form.descriptionHint')}</span>
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="internalNote">
            {t('job.form.internalNote')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <textarea
            id="internalNote"
            className="recform__textarea"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
          />
          <span className="recform__hint">{t('job.form.internalNoteHint')}</span>
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="source">
            {t('job.form.source')}
          </label>
          <select
            id="source"
            className="recform__select"
            value={source}
            onChange={(e) => setSource(e.target.value as (typeof SOURCES)[number])}
          >
            {SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(`job.form.sources.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      <div className="recform__actions">
        <button className="btn btn--primary" type="submit" disabled={busy || !ready}>
          {busy ? t('job.form.saving') : t('job.form.create')}
        </button>
        <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => void navigate({ to: '/jobs' })}>
          {t('common.cancel')}
        </button>
        <span className="recform__spacer" />
      </div>
    </form>
  );
}

/**
 * Find the customer by typing what you know about them.
 *
 * Debounced, and it only asks once there are two characters: a request per keystroke against a
 * full-text index over 6 481 customers is a lot of work to throw away, and one-letter results
 * are never the answer.
 */
function CustomerPicker({
  t,
  onPick,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  onPick: (id: string) => void;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(text.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [text]);

  const results = useQuery({
    queryKey: ['search', 'customer-picker', query],
    queryFn: ({ signal }) => api.search({ q: query, kinds: ['customer'], limit: 8, grouped: false }, signal),
    enabled: query.length >= 2,
  });

  const hits = useMemo(() => results.data?.hits ?? [], [results.data]);

  return (
    <div className="recform__field">
      <label className="recform__label" htmlFor="customerSearch">
        {t('job.form.findCustomer')}
      </label>
      <input
        id="customerSearch"
        className="recform__input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('common.searchPlaceholder')}
        autoComplete="off"
        autoFocus
      />

      {query.length >= 2 && !results.isPending && hits.length === 0 && (
        <p className="recform__notice">{t('job.form.noCustomer')}</p>
      )}

      {hits.length > 0 && (
        <div className="recform__repeat" role="listbox" aria-label={t('job.form.findCustomer')}>
          {hits.map((hit: SearchHit) => {
            const payload = hit.payload as CustomerHitPayload;
            return (
              <button
                key={hit.id}
                type="button"
                role="option"
                aria-selected={false}
                className="btn btn--ghost"
                onClick={() => onPick(hit.id)}
              >
                <span>{hit.title}</span>
                {hit.subtitle && <span className="recform__hint">{hit.subtitle}</span>}
                {payload.city && <span className="recform__hint">{payload.city}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
